import { useFormikContext } from "formik";
import React, { useEffect, useState } from "react";
import Block from "@/components/block";
import Icon from "@/components/icon";
import Input from "@/components/input";
import List from "@/components/list";
import Modal from "@/components/modal";
import Spinner from "@/components/spinner";
import { Body16 } from "@/components/text";
import useDebounce from "@/modules/hooks/use-debounce";
import { colors, spacings } from "../assets/themes";
import { useGetAddressDetails, useGetAddresses } from "../modules/routes/location-routes";
import { uuid } from "../utils/index";
import polyglot from "../utils/polyglot";
import ManualAddress from "./address-modal/manual-address";

const Wrapper = ({ children, isOpen, handleClose, inContent }) =>
  inContent ? (
    <div>{children}</div>
  ) : (
    <Modal.Small fullScreenOnMobile isOpen={isOpen} onClose={handleClose}>
      {children}
    </Modal.Small>
  );

const AddressModal = ({
  onClose,
  isDestination,
  addresses,
  inContent,
  isOpen = inContent,
  onChange,
  onBack,
}) => {
  const { values, setFieldTouched, setValues, validateForm } = useFormikContext();
  const [term, setTerm] = useState("");
  const [status, setStatus] = useState("");
  const [showManualAddress, setShowManualAddress] = useState(false);
  const [sessionToken, setSessionToken] = useState(uuid());
  const prefix = isDestination ? "destination_" : "";
  const debouncedTerm = useDebounce(term);

  const getAddresses = useGetAddresses();
  const data = getAddresses.data?.data || null;
  const isLoading = getAddresses.isPending || debouncedTerm !== term;

  useEffect(() => {
    if (debouncedTerm === term && !!debouncedTerm) {
      getAddresses.mutate({ term, session_token: sessionToken });
    }
  }, [debouncedTerm, term, sessionToken, getAddresses.mutate]);

  const handleSelection = ({
    place_id,
    address_id,
    city_id,
    address_name,
    label,
    location,
    lift,
    floor,
    property_type,
  }) => {
    let newVal = {};
    if (place_id) {
      newVal = {
        [`${prefix}place_id`]: place_id,
        [`${prefix}address_id`]: null,
        [`${prefix}address_name`]: null,
        [`${prefix}city_id`]: null,
      };
    } else if (address_id) {
      newVal = {
        [`${prefix}place_id`]: null,
        [`${prefix}address_id`]: address_id,
        [`${prefix}address_name`]: null,
        [`${prefix}city_id`]: null,
      };
    } else if (city_id && address_name && label) {
      newVal = {
        [`${prefix}place_id`]: null,
        [`${prefix}address_id`]: null,
        [`${prefix}address_name`]: address_name,
        [`${prefix}city_id`]: city_id,
      };
    }
    newVal = {
      ...newVal,
      [`${prefix}address`]: label,
      [`${prefix}address_location`]: location,
      [`${prefix}lift`]: lift || null,
      [`${prefix}floor`]: floor || null,
      [`${prefix}property_type`]: property_type || null,
    };
    setValues({ ...values, ...newVal });
    if (onChange) onChange(newVal);
    setFieldTouched(`${prefix}address`, true, false);
    setTimeout(() => validateForm(), 0);
  };

  const handleClose = () => {
    setShowManualAddress(false);
    setStatus("");
    setTerm("");
    if (onBack) onBack();
    else if (onClose) onClose();
  };

  const checkAddressSuccess = ({ place_id, label, location }) => {
    handleSelection({ place_id, label, location });
    handleClose();
  };

  const checkAddressError = () => {
    setShowManualAddress(true);
    setStatus(polyglot.t("common.address_incomplete"));
    setTerm("");
  };

  const getAddressDetails = useGetAddressDetails({
    onSuccess: checkAddressSuccess,
    onError: checkAddressError,
  });

  const handleSelect = ({ place_id, label }) => {
    getAddressDetails.mutate({
      place_id,
      label,
      session_token: sessionToken,
    });
    setSessionToken(uuid());
  };

  const handleManualSubmit = (values) => {
    handleSelection({
      label: `${values.address_name}, ${values.zip_name}`,
      city_id: values.city_id,
      address_name: values.address_name,
      location: values.location,
    });
    handleClose();
  };

  const handleChange = (value = "") => {
    setTerm(value);
  };

  const listLeftComponent = () => <Icon.Large color={colors.primary} name="map-marker-solid" />;

  return (
    <Wrapper handleClose={handleClose} inContent={inContent} isOpen={isOpen}>
      {!showManualAddress && (
        <>
          <Modal.Item.Header
            onBack={inContent && onBack}
            onClose={handleClose}
            title={polyglot.t("common.enter_address")}
          />
          <Modal.Item.Wrapper className="mt-2" style={{ flex: "inherit" }}>
            <Input
              autoFocus
              onChange={(e) => handleChange(e.target.value)}
              placeholder={polyglot.t("common.placeholder.search_address")}
              RightComponent={
                isLoading && term ? () => <Spinner.Small color={colors.muted} /> : null
              }
              type="search"
              value={term}
            />
          </Modal.Item.Wrapper>
          {getAddresses.isSuccess && (
            <Block data-test="addresses-result" marginTop={spacings.m}>
              {data.map(({ id, description }) => (
                <List.Item
                  chevron
                  isLoading={
                    getAddressDetails.isPending && getAddressDetails.variables.place_id === id
                  }
                  key={id}
                  LeftComponent={() => listLeftComponent()}
                  onClick={() => {
                    handleSelect({ place_id: id, label: description });
                  }}
                  withGutters
                >
                  <List.Elem.Title>{description}</List.Elem.Title>
                </List.Item>
              ))}
              <List.Item
                LeftComponent={() => <Icon.Large color={colors.primary} name="plus-circle" />}
                onClick={() => {
                  setShowManualAddress(true);
                }}
                withGutters
              >
                <List.Elem.Title color={colors.primary} strong>
                  {polyglot.t("common.add_manual_address")}
                </List.Elem.Title>
              </List.Item>
            </Block>
          )}
          {getAddresses.isError && (
            <div className="d-flex justify-content-center mt-4 flex-column align-items-center">
              <Body16 color={colors.muted}>{polyglot.t("common.no_result")}</Body16>
              <button
                className="btn btn-link"
                onClick={() => {
                  setShowManualAddress(true);
                }}
                type="button"
              >
                {polyglot.t("common.manual_address")}
              </button>
            </div>
          )}
          {!data && addresses?.length > 0 && (
            <>
              <List.Header title={polyglot.t("common.last_address")} withGutters />
              {addresses.map(
                (
                  {
                    id,
                    address,
                    location,
                    place_id,
                    city_id,
                    address_name,
                    lift,
                    floor,
                    property_type,
                  },
                  i
                ) => (
                  <List.Item
                    chevron
                    isLoading={
                      getAddressDetails.isPending && getAddressDetails.variables.place_id === id
                    }
                    key={`known-addresses-selection-item-${i}`}
                    LeftComponent={() => listLeftComponent()}
                    onClick={() => {
                      handleSelection({
                        place_id,
                        address_id: id,
                        label: address,
                        lift,
                        floor,
                        property_type,
                        location,
                        address_name,
                        city_id,
                      });
                      handleClose();
                    }}
                    withGutters
                  >
                    <List.Elem.Title>{address}</List.Elem.Title>
                  </List.Item>
                )
              )}
            </>
          )}
        </>
      )}
      {showManualAddress && (
        <>
          <ManualAddress
            onBack={() => {
              setShowManualAddress(false);
              setStatus("");
            }}
            onSubmit={handleManualSubmit}
            status={status}
          />
        </>
      )}
    </Wrapper>
  );
};

const areEqual = (p, n) => p.isOpen === n.isOpen;
export default React.memo(AddressModal, areEqual);
